import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { AssetBasicModel } from '@customer/store/apis/assetsApi';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CustomInvestigationForm.module.scss';
import { useController } from 'react-hook-form';
import { z } from 'zod';
import CasFormDropdown from '@cas/components/inputs/cas-form-dropdown/CasFormDropdown';
import { AnalysisMode } from '@customer/store/apis/investigationsApi';
import {
  ServerErrorFields,
  useMutationForm,
  UseMutationFormProps,
  MutationForm,
  MutationFormProps,
} from '@dmc/ui';
import { CasFormCheckbox } from '@cas/components/inputs/cas-form-checkbox/CasFormCheckbox';
import { AssertTrue, IsBiDiCompatible } from '@cas/utils/typeHelpers';
import { ApiBaseQueryFn } from '@customer/store/api';
import { useCustomInvestigationSchema } from './useCustomInvestigationSchema';
import { useAnalysisModeOptions } from './useAnalysisModeOptions';
import { useStartTimeFields } from './startTimes';
import { AssetSelectionTable } from '../table/AssetSelectionTable';
import {
  AnalysisModeLimits,
  MAX_INVESTIGATION_NAME,
  MAX_INVESTIGATION_PREVIOUS_DAYS,
} from '../config';
import classNames from 'classnames';

export interface CustomInvestigationInputFields {
  name: string;
  startDate: string | null;
  startTime: string | null;
  duration: number;
  analysisMode: AnalysisMode;
  oneSecLog: boolean;
  assetIds: number[];
}

export interface CustomInvestigationOutputFields extends CustomInvestigationInputFields {
  startDate: string;
  startTime: string;
}

type InputFields = CustomInvestigationInputFields;
type OutputFields = CustomInvestigationOutputFields;

const serverErrorFields: ServerErrorFields<InputFields> = {
  name: 'n',
  startDate: 'start',
  duration: 'end',
  assetIds: 'assets',
};

interface CustomInvestigationFormProps<TMutationArgs extends object, TMutationResponse = void>
  extends Pick<
    UseMutationFormProps<
      ApiBaseQueryFn,
      InputFields,
      OutputFields,
      TMutationArgs,
      TMutationResponse
    >,
    'triggerMutation' | 'getArgs' | 'successCallback'
  > {
  defaultValues: InputFields;
  assets: AssetBasicModel[] | undefined;
  validateOnLoad?: boolean; // Provide true if the form validation should be triggered when the defaultValues prop is truthy.
  submitLabel: MutationFormProps<InputFields, OutputFields>['submitLabel'];
  sliceKey?: string; // Provide a sliceKey for individual investigations.
}

export const CustomInvestigationForm = <TMutationArgs extends object, TMutationResponse = void>({
  assets,
  defaultValues,
  triggerMutation,
  getArgs,
  successCallback,
  validateOnLoad,
  submitLabel,
  sliceKey,
}: CustomInvestigationFormProps<TMutationArgs, TMutationResponse>): ReactElement => {
  const { t } = useTranslation();
  // Track the analysis mode in separate state so that the rest of the form can be redefined when it is modified.
  const [mode, setMode] = useState<AnalysisMode>(defaultValues.analysisMode);
  const previousMode = useRef<AnalysisMode>(defaultValues.analysisMode);

  const { startTimeInterval, maxDuration, maxAssets } = AnalysisModeLimits[mode];

  const schema = useCustomInvestigationSchema({ mode });

  // Use type assignment to compare expected output against schema shape during compilation.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  type SchemaVerified = AssertTrue<IsBiDiCompatible<z.infer<typeof schema>, OutputFields>>;

  const serverErrorOverrides = useMemo<ServerErrorFields<InputFields>>(
    () => ({
      name: t('Validation.Maximum', { count: MAX_INVESTIGATION_NAME, unit: 'character' }),
      startDate: t('Validation.LimitExceeded', {
        count: MAX_INVESTIGATION_PREVIOUS_DAYS,
        field: 'Day',
      }),
      duration: t('Validation.Maximum', {
        count: maxDuration,
        unit: 'minute',
        context: 'short',
      }),
      assetIds: t('Validation.InvalidMultipleSelection', { field: 'Asset' }),
    }),
    [t, maxDuration],
  );

  const { canSubmit, onSubmit, errorMessage, control, watch, trigger } = useMutationForm({
    defaultValues,
    schema,
    triggerMutation,
    getArgs,
    successCallback,
    serverErrorFields,
    serverErrorOverrides,
    validateOnLoad,
  });

  const analysisModeOptions = useAnalysisModeOptions();

  const { startDayOptions, startDateTimeOptions } = useStartTimeFields<InputFields, OutputFields>({
    days: MAX_INVESTIGATION_PREVIOUS_DAYS,
    interval: startTimeInterval,
    control,
    watch,
    trigger,
    startDateFieldName: 'startDate',
    startTimeFieldName: 'startTime',
  });

  // Watch independent fields that will impact other fields.
  const selectedDay = watch('startDate');
  const selectedMode = watch('analysisMode');

  // Get dependent fields that might need to be modified.
  const { field: oneSecLogField } = useController({ control, name: 'oneSecLog' });

  // Keep local mode in sync with the form field mode.
  useEffect(() => {
    if (selectedMode !== mode) {
      setMode(selectedMode);
    }
  }, [selectedMode, mode]);

  /**
   * When the local mode state is changed, update/reset any populated fields with mode-specific validation rules.
   * Perform this effect using the local mode (not the watched analysisMode).
   * This ensures that the dependent fields are re-validated only after the new schema is set.
   * Use a ref of the previous mode so that the effect only runs when the mode is changed, not on initial render or dependency change.
   */
  useEffect(() => {
    if (previousMode.current !== mode) {
      previousMode.current = mode;

      if (mode === AnalysisMode.Basic && oneSecLogField.value === true) {
        oneSecLogField.onChange(false);
      }

      // Force validation of fields that have different rules depending on the mode.
      // 'startTime' is handled in useStartTimeFields.
      trigger(['duration', 'assetIds']);
    }
  }, [mode, oneSecLogField, trigger]);

  return (
    <MutationForm
      control={control}
      fieldsClassName={classNames(
        styles.stretchToFill,
        errorMessage && styles['stretchToFill--withError'],
      )}
      errorTitle={t('Error')}
      submitLabel={submitLabel}
      canSubmit={canSubmit}
      onSubmit={onSubmit}
      errorMessage={errorMessage}
    >
      <div className={styles.allFields}>
        <div className={styles.normalFields}>
          <CasFormInput
            control={control}
            name="name"
            label={t('Field.InvestigationName.Label')}
            helperText={t('Validation.Maximum', {
              count: MAX_INVESTIGATION_NAME,
              unit: 'character',
            })}
          />
          <div className={styles.analysisFields}>
            <div className={styles.analysisMode}>
              <CasFormDropdown
                control={control}
                name="analysisMode"
                label={t('Field.AnalysisMode.Label')}
                tooltipLabel={t('Field.AnalysisMode.TooltipLabel')}
                items={analysisModeOptions}
              />
            </div>
            <div className={styles.oneSecondLogs}>
              <CasFormCheckbox
                control={control}
                name="oneSecLog"
                label={t('Field.OneSecondLogs.Label')}
                disabled={mode === AnalysisMode.Basic}
              />
            </div>
          </div>
          <div className={styles.timeFields}>
            <CasFormDropdown
              control={control}
              name="startDate"
              label={t('Field.StartDate.Label')}
              items={startDayOptions}
              helperText={t('LastMonths', { val: 3 })}
            />
            <CasFormDropdown
              control={control}
              name="startTime"
              label={t('Field.StartTime.Label')}
              items={startDateTimeOptions}
              disabled={selectedDay === null || startDateTimeOptions.length === 0}
            />
            <CasFormInput
              control={control}
              name="duration"
              type="number"
              label={t('AnnotatedField', { field: 'Duration', unit: 'minute', context: 'short' })}
              helperText={t('Validation.Maximum', {
                count: maxDuration,
                unit: 'minute',
                context: 'short',
              })}
            />
          </div>
        </div>
        <AssetSelectionTable
          control={control}
          assets={assets}
          defaultSelection={defaultValues.assetIds}
          sliceKey={sliceKey}
          maxSelection={maxAssets}
        />
      </div>
    </MutationForm>
  );
};
